import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { fetchFacilityProcessOptions } from '@/api/dropdowns';

export interface FacilityProcessOption {
  key: string;
  label: string;
}

export interface FacilityProcessGroupOption {
  id: string;
  label: string;
  options: FacilityProcessOption[];
}

const STALE = 24 * 60 * 60 * 1000;

async function fetchGroupedFacilityProcessOptions(): Promise<
  FacilityProcessGroupOption[]
> {
  const res = await fetchFacilityProcessOptions();
  const groups: FacilityProcessGroupOption[] = [];
  for (const g of res?.data ?? []) {
    const id = g.productionProcessId ?? g.productionProcessName ?? '';
    const label = g.productionProcessName ?? id ?? '';
    groups.push({
      id,
      label,
      options: g.children.map(c => ({ key: c.key, label: c.text })),
    });
  }
  return groups;
}

export const groupedFacilityProcessOptionsQueryOptions = {
  queryKey: ['options', 'facility-processes', 'grouped'] as const,
  queryFn: fetchGroupedFacilityProcessOptions,
  staleTime: STALE,
};

export function useGroupedFacilityProcessOptions() {
  const query = useQuery(groupedFacilityProcessOptionsQueryOptions);

  const groups = query.data;
  // Stable while the query data doesn't change — resolveLabel feeds
  // column-def memos, so a per-render identity would void them.
  const resolveLabel = useCallback(
    (key: string): string => {
      for (const g of groups ?? []) {
        const opt = g.options.find(o => o.key === key);
        if (opt) return opt.label;
      }
      return key;
    },
    [groups],
  );

  return { ...query, resolveLabel };
}
